import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, Bot, Send, CheckCircle2, XCircle, AlertTriangle, RefreshCcw,
  Download, FileText, ShieldCheck, Sparkles, ClipboardList, Bell, Users2,
  BookOpenCheck, Mail, Play, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/ist";
import AdminOpsProjects from "./AdminOpsProjects";
import AdminOpsBugs from "./AdminOpsBugs";
import AdminOpsKB from "./AdminOpsKB";
import AdminAIOpsWorkflow from "./AdminAIOpsWorkflow";
import AdminAIWork from "./AdminAIWork";

/* AI Operations Command Center — light theme (green / white / orange) */

const RISK_COLORS = {
  low: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700", critical: "bg-rose-100 text-rose-700",
};
const SEV_COLORS = {
  minor: "bg-slate-100 text-slate-600", moderate: "bg-amber-100 text-amber-700",
  major: "bg-orange-100 text-orange-700", critical: "bg-rose-100 text-rose-700",
  emergency: "bg-rose-200 text-rose-800",
};
const Chip = ({ map, v }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${map[v] || "bg-slate-100 text-slate-600"}`}>{v}</span>
);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const downloadAuthed = async (path, filename) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);

/* ───────────────────────── Overview ───────────────────────── */

function Overview({ setTab }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/ai-ops/overview").then(r=>setData(r.data)).catch(()=>toast.error("Failed to load overview"));
  }, []);
  useEffect(load, [load]);
  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-emerald-600"/></div>;
  const s = data.snapshot;
  const cards = [
    ["Collected today", inr((s.payments.sm888_collected_today || 0) + (s.payments.links_collected_today || 0)), "text-emerald-600"],
    ["Pending payments", s.orders.sm888_pending_payment + s.orders.payment_links_active, "text-amber-600"],
    ["Open tickets", s.support.open_tickets, "text-orange-600"],
    ["Pending approvals", s.ai.pending_approvals, "text-rose-600"],
    ["Open AI alerts", s.ai.open_alerts, s.ai.critical_alerts ? "text-rose-600" : "text-slate-700"],
    ["AI actions today", s.ai.actions_today, "text-emerald-600"],
  ];
  return (
    <div className="space-y-5" data-testid="aiops-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(([t, v, c]) => (
          <Card key={t} className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{t}</div>
            <div className={`text-2xl font-extrabold mt-1 ${c}`} style={{fontFamily:"Outfit"}}>{v}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-900 inline-flex items-center gap-2"><Sparkles size={16} className="text-orange-500"/> Morning Operations Summary · {data.report_date}</h3>
          <div className="flex gap-2">
            <button onClick={async()=>{ try { await api.post("/admin/ai-ops/summary/send"); toast.success("Summary emailed to admin"); } catch(e){ toast.error(e?.response?.data?.detail || "Send failed"); } }}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="aiops-send-summary"><Mail size={12}/> Email now</button>
            <button onClick={()=>downloadAuthed(`/admin/ai-ops/reports/${data.report_date}/pdf`, `optivize-daily-report-${data.report_date}.pdf`)}
              className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="aiops-report-pdf"><FileText size={12}/> Full report PDF</button>
          </div>
        </div>
        <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {data.summary_lines.map((l, i) => (
            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"/>{l}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 mt-4">
          {[["approvals","View Pending Approvals"],["alerts","View Critical Alerts"],["reports","Download Full Report"],["command","Ask the AI"]].map(([t,l]) => (
            <button key={t} onClick={()=>setTab(t)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:border-emerald-400 text-xs font-semibold" data-testid={`aiops-quick-${t}`}>{l}</button>
          ))}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm inline-flex items-center gap-1.5"><Bell size={14} className="text-rose-500"/> Latest alerts</h4>
          {data.alerts.length === 0 ? <p className="text-xs text-slate-400 mt-3">No open alerts — all clear ✅</p> :
            data.alerts.map(a => (
              <div key={a.id} className="mt-2.5 text-xs text-slate-600 flex items-start gap-2">
                <Chip map={SEV_COLORS} v={a.severity}/><span className="flex-1">{a.message}</span>
              </div>
            ))}
        </Card>
        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600"/> Awaiting your approval</h4>
          {data.approvals.length === 0 ? <p className="text-xs text-slate-400 mt-3">Approval queue is empty.</p> :
            data.approvals.map(p => (
              <div key={p.id} className="mt-2.5 text-xs text-slate-600 flex items-start gap-2">
                <Chip map={RISK_COLORS} v={p.risk_level}/><span className="flex-1">{p.title}</span>
              </div>
            ))}
        </Card>
      </div>
    </div>
  );
}

/* ───────────────────────── Command box ───────────────────────── */

const SUGGESTIONS = [
  "Summarize today's operations",
  "Show all pending payments",
  "Which support tickets are open and what should I prioritise?",
  "Prepare a follow-up email for customers with pending payments",
  "Any payment reconciliation problems right now?",
];

const parseEmailDraft = (content) => {
  const m = content.match(/```EMAIL_DRAFT\s*([\s\S]*?)```/);
  if (!m) return { text: content, draft: null };
  let draft = null;
  try { draft = JSON.parse(m[1].trim()); } catch { /* leave raw */ }
  return { text: content.replace(m[0], "").trim(), draft };
};

function DraftCard({ draft }) {
  const [to, setTo] = useState(draft.to && draft.to.includes("@") ? draft.to : "");
  const [queued, setQueued] = useState(false);
  const [busy, setBusy] = useState(false);
  const queue = async () => {
    if (!to.includes("@")) { toast.error("Enter the recipient email"); return; }
    setBusy(true);
    try {
      await api.post("/admin/ai-ops/approvals", {
        action_type: "send_customer_email",
        title: `Send email: “${draft.subject}” → ${to}`,
        description: `AI-drafted customer email. It will be sent automatically when you approve it.\n\n${(draft.body_html || "").replace(/<[^>]+>/g, " ").slice(0, 500)}`,
        ai_recommendation: "Drafted by the Customer Communication Agent from the AI Command box. Review wording before approving.",
        risk_level: "medium",
        payload: { to, subject: draft.subject, body_html: draft.body_html },
      });
      setQueued(true);
      toast.success("Draft queued — approve it in the Approvals tab to send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to queue"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-xl border-2 border-orange-200 bg-orange-50/60 p-3" data-testid="aiops-email-draft">
      <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold inline-flex items-center gap-1"><Mail size={11}/> Email draft</div>
      <div className="text-sm font-bold text-slate-900 mt-1">{draft.subject}</div>
      <div className="text-xs text-slate-600 mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: draft.body_html }}/>
      <div className="flex gap-2 mt-2.5 items-center">
        <input value={to} onChange={(e)=>setTo(e.target.value)} placeholder="recipient@email.com"
          className="flex-1 px-2.5 py-1.5 rounded-lg border border-orange-200 text-xs" data-testid="aiops-draft-to"/>
        <button onClick={queue} disabled={busy || queued}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1 ${queued ? "bg-emerald-500" : "bg-orange-500 hover:bg-orange-600"} disabled:opacity-70`}
          data-testid="aiops-draft-queue">
          {queued ? <><CheckCircle2 size={12}/> Queued for approval</> : busy ? "Queuing…" : "Queue for approval"}
        </button>
      </div>
    </div>
  );
}

function CommandBox() {
  const [sessionId] = useState(() => {
    const k = "aiops_session";
    let v = sessionStorage.getItem(k);
    if (!v) { v = `s-${Date.now()}-${Math.random().toString(36).slice(2,8)}`; sessionStorage.setItem(k, v); }
    return v;
  });
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/admin/ai-ops/command/${sessionId}`).then(r=>setMsgs(r.data.rows)).catch(()=>{});
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setMsgs(m => [...m, { id: Math.random(), role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-ops/command", { session_id: sessionId, message });
      setMsgs(m => [...m, { id: Math.random(), role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI command failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="flex flex-col" data-testid="aiops-command">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Bot size={16} className="text-emerald-600"/>
        <span className="font-bold text-slate-900 text-sm">AI Operations Manager</span>
        <span className="text-[10px] text-slate-400 ml-auto">gpt-5-mini · live business data · sensitive actions need approval</span>
      </div>
      <div className="flex-1 min-h-[380px] max-h-[520px] overflow-y-auto p-4 space-y-3 bg-slate-50/50">
        {msgs.length === 0 && (
          <div className="text-center pt-8">
            <Sparkles size={26} className="mx-auto text-orange-400"/>
            <p className="text-sm text-slate-500 mt-2">Ask anything about orders, payments, tickets or reports.</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4 max-w-lg mx-auto">
              {SUGGESTIONS.map(sug => (
                <button key={sug} onClick={()=>send(sug)} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-emerald-400 text-xs text-slate-600" data-testid="aiops-suggestion">{sug}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map(m => {
          const { text, draft } = m.role === "assistant" ? parseEmailDraft(m.content) : { text: m.content, draft: null };
          return (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                {text}
                {draft && <DraftCard draft={draft}/>}
              </div>
            </div>
          );
        })}
        {busy && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={13} className="animate-spin"/> Analysing live operations…</div>}
        <div ref={endRef}/>
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <input value={input} onChange={(e)=>setInput(e.target.value)}
          onKeyDown={(e)=>{ if (e.key === "Enter") send(); }}
          placeholder='e.g. "Show overdue orders" or "Generate today&apos;s payment report"'
          className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-400 focus:outline-none text-sm" data-testid="aiops-command-input"/>
        <button onClick={()=>send()} disabled={busy || !input.trim()}
          className="px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 disabled:opacity-40 text-white inline-flex items-center gap-1.5 text-sm font-bold" data-testid="aiops-command-send">
          <Send size={14}/>
        </button>
      </div>
    </Card>
  );
}

/* ───────────────────────── Approvals ───────────────────────── */

function Approvals() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(() => {
    api.get("/admin/ai-ops/approvals", { params: filter ? { status: filter } : {} })
      .then(r=>setRows(r.data.rows)).catch(()=>toast.error("Failed to load"));
  }, [filter]);
  useEffect(load, [load]);
  const decide = async (id, approve) => {
    const comments = window.prompt(approve ? "Approval comments (optional):" : "Rejection reason (optional):") || null;
    try { await api.post(`/admin/ai-ops/approvals/${id}/decide`, { approve, comments }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="aiops-approvals">
      <div className="flex gap-2 mb-4">
        {["pending","approved","rejected",""].map(f => (
          <button key={f || "all"} onClick={()=>setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${filter===f ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500"}`}
            data-testid={`aiops-approvals-filter-${f || "all"}`}>{f || "all"}</button>
        ))}
      </div>
      {!rows ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> :
        rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400">No {filter || ""} approval requests.</Card> : (
        <div className="space-y-3">
          {rows.map(p => (
            <Card key={p.id} className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Chip map={RISK_COLORS} v={p.risk_level}/>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{p.action_type}</span>
                    <span className="text-[10px] text-slate-400">{fmtDateTime(p.requested_at)} · by {p.requested_by}</span>
                  </div>
                  <div className="font-bold text-slate-900 mt-1">{p.title}</div>
                  {p.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{p.description}</p>}
                  {p.ai_recommendation && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-800">
                      <b>AI recommendation:</b> {p.ai_recommendation}
                    </div>
                  )}
                  {p.status !== "pending" && (
                    <div className="text-xs text-slate-500 mt-2">
                      {p.status === "approved" ? "✅ Approved" : "❌ Rejected"} by {p.decided_by} · {fmtDateTime(p.decided_at)}
                      {p.admin_comments && <> · “{p.admin_comments}”</>}
                      {p.action_type === "send_customer_email" && p.status === "approved" && (
                        p.payload?.sent_at
                          ? <span className="ml-2 text-emerald-600 font-bold">📧 Email sent {fmtDateTime(p.payload.sent_at)}</span>
                          : <span className="ml-2 text-rose-600 font-bold">📧 Email send failed — check SMTP / recipient</span>
                      )}
                    </div>
                  )}
                </div>
                {p.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={()=>decide(p.id, true)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`aiops-approve-${p.id.slice(0,6)}`}><CheckCircle2 size={13}/> Approve</button>
                    <button onClick={()=>decide(p.id, false)} className="px-3 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`aiops-reject-${p.id.slice(0,6)}`}><XCircle size={13}/> Reject</button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Alerts ───────────────────────── */

function Alerts() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/ai-ops/alerts", { params: { status: "open" } }).then(r=>setRows(r.data.rows)).catch(()=>toast.error("Failed"));
  }, []);
  useEffect(load, [load]);
  const runSweep = async () => {
    try { const { data } = await api.post("/admin/ai-ops/sweeps/run"); toast.success(`Sweep done · ${data.reconciliation.alerts_created || 0} new alerts · ${data.followups.reminders_sent || 0} reminders sent`); load(); }
    catch { toast.error("Sweep failed"); }
  };
  return (
    <div data-testid="aiops-alerts">
      <div className="flex justify-end mb-3">
        <button onClick={runSweep} className="px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="aiops-run-sweep"><Play size={12}/> Run reconciliation & follow-up sweep now</button>
      </div>
      {!rows ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> :
        rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400">No open alerts. The Payment & Invoice Agent found nothing to flag ✅</Card> : (
        <div className="space-y-2.5">
          {rows.map(a => (
            <Card key={a.id} className="p-3.5 flex items-start gap-3">
              <AlertTriangle size={16} className={a.severity === "critical" || a.severity === "emergency" ? "text-rose-500 mt-0.5" : "text-amber-500 mt-0.5"}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip map={SEV_COLORS} v={a.severity}/>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{a.type}</span>
                  <span className="text-[10px] text-slate-400">{fmtDateTime(a.created_at)} · agent: {a.agent}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1">{a.message}</p>
              </div>
              <button onClick={async()=>{ try { await api.post(`/admin/ai-ops/alerts/${a.id}/resolve`); toast.success("Alert resolved"); load(); } catch { toast.error("Failed"); } }}
                className="px-3 py-1.5 rounded-lg border border-slate-200 hover:border-emerald-400 text-xs font-semibold text-slate-600 shrink-0" data-testid={`aiops-resolve-${a.id.slice(0,6)}`}>Resolve</button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Agents ───────────────────────── */

function Agents() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/ai-ops/agents").then(r=>setData(r.data));
    api.get("/admin/ai-ops/settings").then(r=>setSettings(r.data));
  }, []);
  useEffect(load, [load]);
  if (!data || !settings) return <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/>;
  const labels = data.autonomy_labels;
  const saveSettings = async () => {
    try { await api.put("/admin/ai-ops/settings", settings); toast.success("AI Ops settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-4" data-testid="aiops-agents">
      <Card className="p-4">
        <h4 className="font-bold text-slate-900 text-sm mb-3">Global autonomy & automation</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500">Default autonomy level</span>
            <select value={settings.default_autonomy} onChange={(e)=>setSettings(s=>({...s, default_autonomy:Number(e.target.value)}))}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 bg-white" data-testid="aiops-default-autonomy">
              {[1,2,3,4].map(l => <option key={l} value={l}>Level {l} — {labels[l]}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Morning summary email to</span>
            <input value={settings.morning_email_to} onChange={(e)=>setSettings(s=>({...s, morning_email_to:e.target.value}))}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200" data-testid="aiops-morning-email"/>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500">Max follow-up reminders</span>
            <input type="number" min={1} max={5} value={settings.followup_max_reminders} onChange={(e)=>setSettings(s=>({...s, followup_max_reminders:Number(e.target.value)}))}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200" data-testid="aiops-max-reminders"/>
          </label>
          <div className="flex items-end gap-3 pb-0.5">
            {[["morning_email_enabled","Morning email"],["followups_enabled","Follow-ups"],["reconciliation_enabled","Reconciliation"]].map(([k,l]) => (
              <label key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={!!settings[k]} onChange={(e)=>setSettings(s=>({...s, [k]:e.target.checked}))} className="accent-emerald-600" data-testid={`aiops-set-${k}`}/>{l}
              </label>
            ))}
          </div>
        </div>
        <button onClick={saveSettings} className="mt-3 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="aiops-save-settings"><Save size={12}/> Save settings</button>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        {data.agents.map(a => (
          <Card key={a.key} className={`p-4 ${!a.enabled && "opacity-60"}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-slate-900 text-sm inline-flex items-center gap-1.5">
                  <Bot size={14} className={a.key === "ops_manager" ? "text-orange-500" : "text-emerald-600"}/>{a.name}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">{a.role}</div>
              </div>
              <button onClick={async()=>{ await api.patch(`/admin/ai-ops/agents/${a.key}`, { enabled: !a.enabled }); load(); }}
                className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${a.enabled ? "bg-emerald-500" : "bg-slate-300"}`} data-testid={`aiops-agent-toggle-${a.key}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${a.enabled ? "left-[18px]" : "left-0.5"}`}/>
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">{a.description}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(a.permissions || []).map(p => <span key={p} className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{p}</span>)}
            </div>
            <div className="mt-3">
              <select value={a.autonomy_level ?? ""} onChange={async(e)=>{ 
                  const v = e.target.value;
                  await api.patch(`/admin/ai-ops/agents/${a.key}`, v === "" ? { clear_autonomy: true } : { autonomy_level: Number(v) });
                  load();
                }}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid={`aiops-agent-autonomy-${a.key}`}>
                <option value="">Inherit default (Level {data.default_autonomy})</option>
                {[1,2,3,4].map(l => <option key={l} value={l}>Level {l} — {labels[l]}</option>)}
              </select>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── Reports & Audit ───────────────────────── */

function Reports() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get("/admin/ai-ops/reports").then(r=>setRows(r.data.rows)); }, []);
  useEffect(load, [load]);
  return (
    <div data-testid="aiops-reports">
      <div className="flex justify-end mb-3">
        <button onClick={async()=>{ await api.post("/admin/ai-ops/reports/generate"); toast.success("Report regenerated"); load(); }}
          className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="aiops-gen-report"><RefreshCcw size={12}/> Regenerate today&apos;s report</button>
      </div>
      {!rows ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> :
        rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400">No reports yet.</Card> : (
        <div className="space-y-2.5">
          {rows.map(r => (
            <Card key={r.id} className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-bold text-slate-900 text-sm">{r.date}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.orders?.new_orders_today} new orders · {inr(r.payments?.collected_today)} collected · {r.support?.open_tickets} open tickets · {r.ai?.approvals_pending} approvals pending
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>downloadAuthed(`/admin/ai-ops/reports/${r.date}/pdf`, `optivize-daily-report-${r.date}.pdf`)} className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`aiops-pdf-${r.date}`}><FileText size={12}/> PDF</button>
                <button onClick={()=>downloadAuthed(`/admin/ai-ops/reports/${r.date}/csv`, `optivize-daily-report-${r.date}.csv`)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold inline-flex items-center gap-1" data-testid={`aiops-csv-${r.date}`}><Download size={12}/> CSV</button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/ai-ops/audit").then(r=>setRows(r.data.rows)).catch(()=>setRows([])); }, []);
  if (!rows) return <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/>;
  return (
    <Card className="overflow-x-auto" data-testid="aiops-audit">
      {rows.length === 0 ? <p className="text-sm text-slate-400 p-8 text-center">No audit entries yet.</p> : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider border-b border-slate-100">
            <tr><th className="text-left px-3 py-2.5">When (IST)</th><th className="text-left px-2">Actor</th><th className="text-left px-2">Action</th><th className="text-left px-2">Detail</th></tr>
          </thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id} className="border-b border-slate-50">
                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                <td className="px-2 font-mono text-emerald-700">{a.actor}</td>
                <td className="px-2 font-mono text-slate-700">{a.action}</td>
                <td className="px-2 text-slate-500 max-w-[380px] truncate">{JSON.stringify(a.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ───────────────────────── Root ───────────────────────── */

const TABS = [
  ["overview", "Overview", Sparkles],
  ["workflow", "Workflow", Bot],
  ["command", "AI Command", Bot],
  ["approvals", "Approvals", ShieldCheck],
  ["alerts", "Alerts", Bell],
  ["projects", "Projects", ClipboardList],
  ["aiwork", "AI Work", Bot],
  ["bugs", "Bugs", AlertTriangle],
  ["kb", "Knowledge", BookOpenCheck],
  ["agents", "Agents", Users2],
  ["reports", "Reports", FileText],
  ["audit", "Audit", RefreshCcw],
];

export default function AdminAIOps() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="rounded-2xl bg-gradient-to-b from-emerald-50/80 via-white to-orange-50/50 border border-emerald-100 p-5 -m-1" data-testid="admin-aiops">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-widest"><Bot size={11}/> AI Operations Agent</div>
          <h1 className="text-2xl font-extrabold text-slate-900 mt-1.5" style={{fontFamily:"Outfit"}}>Optivize AI Operations Command Center</h1>
          <p className="text-slate-500 text-sm">Optivize — A Unit of SURAJIT MAJHI HUF · 13 agents · Level 3 Supervised by default</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={()=>setTab(id)}
              className={`px-3 py-2 rounded-lg text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${tab===id ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-emerald-300"}`}
              data-testid={`aiops-tab-${id}`}><Icon size={13}/>{label}</button>
          ))}
        </div>
      </div>
      {tab === "overview" && <Overview setTab={setTab}/>}
      {tab === "workflow" && <AdminAIOpsWorkflow/>}
      {tab === "command" && <CommandBox/>}
      {tab === "approvals" && <Approvals/>}
      {tab === "alerts" && <Alerts/>}
      {tab === "projects" && <AdminOpsProjects/>}
      {tab === "aiwork" && <AdminAIWork/>}
      {tab === "bugs" && <AdminOpsBugs/>}
      {tab === "kb" && <AdminOpsKB/>}
      {tab === "agents" && <Agents/>}
      {tab === "reports" && <Reports/>}
      {tab === "audit" && <Audit/>}
    </div>
  );
}
